import React, { useState, useEffect } from 'react';
import { BRAND } from './product';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';

interface InvitationDetails {
  id: number;
  email: string;
  role: string;
  expiresAt: string;
  teamId: number;
  teamName: string;
}

const InvitationAccept: React.FC = () => {
  const { token: inviteToken } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { user, token: authToken } = useAuth();

  const [invitation, setInvitation] = useState<InvitationDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAccepting, setIsAccepting] = useState(false);
  const [error, setError] = useState('');

  const API_BASE = '/api';

  useEffect(() => {
    loadInvitation();
  }, [inviteToken]);

  const loadInvitation = async () => {
    try {
      setIsLoading(true);
      setError('');

      const response = await fetch(`${API_BASE}/teams/invitation/${inviteToken}`);

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        if (response.status === 404) {
          setError(errorData?.error || 'This invitation is not found or has already been accepted.');
        } else if (response.status === 410) {
          setError(errorData?.error || 'This invitation has expired. Please ask your team admin to resend it.');
        } else {
          setError('Failed to load invitation details. Please try again or contact your team admin.');
        }
        return;
      }

      const data = await response.json();
      setInvitation(data);
    } catch (err) {
      console.error('Failed to load invitation:', err);
      setError('Failed to load invitation details.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAcceptInvitation = async () => {
    if (!authToken) {
      // User not logged in - redirect to login with return URL
      const returnUrl = `/invitation/${inviteToken}`;
      navigate(`/?invitation=${inviteToken}`);
      return;
    }

    if (!invitation) return;

    // Check if logged-in user email matches invitation email
    if (user?.email.toLowerCase() !== invitation.email.toLowerCase()) {
      setError(`This invitation is for ${invitation.email}. Please log in with that email address or create a new account.`);
      return;
    }

    try {
      setIsAccepting(true);
      setError('');

      const response = await fetch(`${API_BASE}/teams/invitation/${inviteToken}/accept`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${authToken}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to accept invitation');
      }

      // Success! Reload the page to update user context
      window.location.href = '/team';
    } catch (err: any) {
      console.error('Failed to accept invitation:', err);
      setError(err.message || 'Failed to accept invitation');
    } finally {
      setIsAccepting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="text-4xl mb-4">⏳</div>
          <div className="text-gray-600">Loading invitation...</div>
        </div>
      </div>
    );
  }

  if (error && !invitation) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full mx-4">
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <div className="text-6xl mb-4">❌</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Invalid Invitation</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              onClick={() => navigate('/')}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Go to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!invitation) {
    return null;
  }

  // User is already on a team
  if (user?.teamId) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full mx-4">
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <div className="text-6xl mb-4">⚠️</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Already on a Team</h2>
            <p className="text-gray-600 mb-6">
              You're already a member of a team. You need to leave your current team before joining another one.
            </p>
            <button
              onClick={() => navigate('/team')}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Go to My Team
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show invitation details
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full mx-4">
        <div className="bg-white rounded-lg shadow p-8">
          <div className="text-center mb-6">
            <div className="text-6xl mb-4">👥</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              You've Been Invited!
            </h2>
            <p className="text-gray-600">
              You've been invited to join a team on {BRAND.name}
            </p>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Team:</span>
                <span className="font-medium text-gray-900">{invitation.teamName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Your role:</span>
                <span className="font-medium text-gray-900 capitalize">{invitation.role}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Invited email:</span>
                <span className="font-medium text-gray-900">{invitation.email}</span>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
            <h3 className="font-semibold text-gray-900 mb-2 text-sm">As a {invitation.role}, you can:</h3>
            <ul className="text-sm text-gray-600 space-y-1">
              {invitation.role === 'admin' && (
                <>
                  <li>• View and manage all team projects</li>
                  <li>• Invite and manage team members</li>
                  <li>• Export data and view export logs</li>
                  <li>• Manage team settings</li>
                </>
              )}
              {invitation.role === 'member' && (
                <>
                  <li>• View all team projects</li>
                  <li>• Export data from projects</li>
                  <li>• Create and manage your own projects</li>
                </>
              )}
              {invitation.role === 'viewer' && (
                <>
                  <li>• View all team projects (read-only)</li>
                  <li>• View project data and maps</li>
                </>
              )}
            </ul>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          {!user ? (
            <div className="space-y-3">
              <p className="text-sm text-gray-600 text-center mb-4">
                Please log in or create an account with <strong>{invitation.email}</strong> to accept this invitation.
              </p>
              <button
                onClick={handleAcceptInvitation}
                className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
              >
                Continue to Login
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              <button
                onClick={handleAcceptInvitation}
                disabled={isAccepting}
                className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isAccepting ? 'Accepting...' : 'Accept Invitation'}
              </button>
              <button
                onClick={() => navigate('/')}
                className="w-full px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Decline
              </button>
            </div>
          )}

          <p className="text-xs text-gray-500 text-center mt-6">
            Invitation expires {new Date(invitation.expiresAt).toLocaleDateString()}
          </p>
        </div>
      </div>
    </div>
  );
};

export default InvitationAccept;
